import React, { useCallback } from "react";
import { Box, Button, useMediaQuery } from "@mui/material";
import {
  AssignmentOutlined,
  AssessmentOutlined,
  NotificationsActiveOutlined,
  HelpOutlineRounded,
  PrecisionManufacturingRounded,
  DescriptionOutlined,
  SettingsRounded,
  ArrowBackRounded,
  InfoOutlined,
  ScheduleRounded,
} from "@mui/icons-material";
import useTranslation from "../../hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../hooks/sharedHooks";
import { setMachineContext, openMachinePicker } from "../chatBotSlice";
import MidasLogo from "../../../customer/midas/components/MidasLogo";
import { dt } from "../../themes/redesignTokens";

// ===================================================================
//  MIDAS Design Tokens (local subset)
// ===================================================================


const midas = {
  gold: "#D4A017",
  goldDark: "#A67C00",
  goldSoft: "rgba(212, 160, 23, 0.08)",
};

// ===================================================================
//  Starter Suggestions (platform mode)
// ===================================================================

const platformSuggestions = [
  { icon: AssignmentOutlined, textKey: "suggestions.serviceRequests" },
  { icon: AssessmentOutlined, textKey: "suggestions.report" },
  { icon: HelpOutlineRounded, textKey: "suggestions.capabilities" },
  { icon: NotificationsActiveOutlined, textKey: "suggestions.attention" },
];

// ===================================================================
//  Machine Mode Suggestions
// ===================================================================

const machineSuggestions = [
  {
    icon: DescriptionOutlined,
    textKey: "machineMode.suggestions.serviceReports",
  },
  { icon: SettingsRounded, textKey: "machineMode.suggestions.spareParts" },
  {
    icon: InfoOutlined,
    textKey: "machineMode.suggestions.overview",
    messageKey: "machineMode.suggestions.overviewMessage",
  },
  { icon: ScheduleRounded, textKey: "machineMode.suggestions.serviceInterval" },
];

// ===================================================================
//  ChatWelcome
// ===================================================================

interface ChatWelcomeProps {
  onSuggestionClick: (text: string) => void;
}

const ChatWelcome: React.FC<ChatWelcomeProps> = ({ onSuggestionClick }) => {
  const { translate } = useTranslation("chatBot");
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.account.user);
  const machineContext = useAppSelector((state) => state.chatBot.machineContext);
  const machineContextLoading = useAppSelector(
    (state) => state.chatBot.machineContextLoading
  );
  const isPhone = useMediaQuery("(max-width:768px)");

  const firstName = user?.firstName || "";

  const handleOpenPicker = useCallback(() => {
    dispatch(openMachinePicker());
  }, [dispatch]);

  const handleExitMachineMode = () => {
    if (machineContextLoading) return;
    dispatch(setMachineContext(null));
  };

  // =================================================================
  //  Machine Mode Welcome Screen
  // =================================================================
  if (machineContext) {
    const hasImage = !!machineContext.imageContent;
    const displayName = [machineContext.manufacturerName, machineContext.model]
      .filter(Boolean)
      .join(" ");
    const machineType = [
      machineContext.axisConfigName,
      machineContext.typeName,
    ]
      .filter(Boolean)
      .join(" ");
    const systemLine = [
      machineContext.controlSystemBrandName,
      machineContext.system,
    ]
      .filter(Boolean)
      .join(" ");
    const serialParts: string[] = [];
    if (machineContext.serialNumber) {
      serialParts.push(`S/N: ${machineContext.serialNumber}`);
    }
    if (machineContext.internalNumber) {
      serialParts.push(`Machine#: ${machineContext.internalNumber}`);
    }
    const serialLine = serialParts.join("  \u00B7  ");

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
          padding: "16px 16px 8px",
          overflow: "auto",
        }}
      >
        {/* Machine image or icon — only show if there's an actual image */}
        {hasImage && (
          <Box
            sx={{
              animation:
                "midasWelcomeAvatar 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both",
            }}
          >
            <img
              src={`data:image/jpeg;base64,${machineContext.imageContent}`}
              alt={displayName || machineContext.model}
              style={{
                height: 80,
                maxWidth: 160,
                borderRadius: 10,
                objectFit: "cover",
              }}
            />
          </Box>
        )}

        {/* Machine identity: Manufacturer + Model */}
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "17px",
            fontWeight: 700,
            color: dt.textPrimary,
            textAlign: "center",
            mt: hasImage ? "10px" : "4px",
            animation:
              "midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
          }}
        >
          {displayName || machineContext.model}
        </Box>

        {/* Machine type line: AxisConfig + Type */}
        {machineType && (
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 400,
              color: dt.textSecondary,
              textAlign: "center",
              mt: "2px",
              animation:
                "midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.13s both",
            }}
          >
            {machineType}
          </Box>
        )}

        {/* System line: ControlSystemBrand + System */}
        {systemLine && (
          <Box
            sx={{
              fontFamily: dt.fontMono,
              fontSize: "11px",
              fontWeight: 500,
              color: dt.textMuted,
              textAlign: "center",
              mt: "2px",
              animation:
                "midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
            }}
          >
            System: {systemLine}
          </Box>
        )}

        {/* Serial number + internal number */}
        {serialLine && (
          <Box
            sx={{
              fontFamily: dt.fontMono,
              fontSize: "11px",
              fontWeight: 500,
              color: dt.textSecondary,
              textAlign: "center",
              mt: "2px",
              animation:
                "midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.17s both",
            }}
          >
            {serialLine}
          </Box>
        )}

        {/* Description card */}
        <Box
          sx={{
            maxWidth: "340px",
            width: "100%",
            mt: "12px",
            mx: "auto",
            backgroundColor: dt.inputBg,
            borderRadius: "10px",
            padding: "10px 14px",
            animation:
              "midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both",
          }}
        >
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 400,
              color: dt.textSecondary,
              lineHeight: 1.5,
              textAlign: "center",
            }}
          >
            {translate("machineMode.description")}
          </Box>
        </Box>

        {/* Machine-specific suggestions (2x2 grid) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "6px",
            maxWidth: "340px",
            width: "100%",
            mt: "10px",
            mx: "auto",
          }}
        >
          {machineSuggestions.map((item, index) => {
            const Icon = item.icon;
            const text = translate(item.textKey);
            const message = translate(
              (item as { messageKey?: string }).messageKey || item.textKey
            );
            return (
              <Box
                key={item.textKey}
                onClick={() => onSuggestionClick(message)}
                sx={{
                  backgroundColor: dt.cardBg,
                  border: `1.5px solid ${dt.cardBorder}`,
                  borderRadius: "10px",
                  padding: "10px 12px",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  transition: "all 0.2s ease",
                  animation: `midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) ${
                    0.25 + index * 0.06
                  }s both`,
                  "&:hover": {
                    borderColor: midas.gold,
                    backgroundColor: midas.goldSoft,
                    "& .midas-chip-text": {
                      color: midas.goldDark,
                    },
                  },
                }}
              >
                <Icon sx={{ fontSize: "15px", color: midas.gold }} />
                <Box
                  className="midas-chip-text"
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "11px",
                    fontWeight: 500,
                    color: dt.textSecondary,
                    lineHeight: 1.4,
                    textAlign: "left",
                  }}
                >
                  {text}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Exit machine mode button */}
        <Box
          sx={{
            maxWidth: "340px",
            width: "100%",
            mt: "10px",
            mx: "auto",
            animation:
              "midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.5s both",
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            disabled={machineContextLoading}
            onClick={handleExitMachineMode}
            startIcon={<ArrowBackRounded sx={{ fontSize: "16px" }} />}
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 500,
              textTransform: "none",
              color: dt.textSecondary,
              borderColor: dt.cardBorder,
              borderRadius: "10px",
              padding: "8px 14px",
              "&:hover": {
                borderColor: dt.textSecondary,
                backgroundColor: "rgba(0, 0, 0, 0.03)",
              },
            }}
          >
            {translate("machineMode.backToPlatform")}
          </Button>
        </Box>
      </Box>
    );
  }

  // =================================================================
  //  Platform Mode Welcome Screen (unchanged)
  // =================================================================

  const greeting = translate("welcomeTitle", { firstName });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        padding: "24px 16px",
      }}
    >
      {/* Avatar */}
      <Box
        sx={{
          animation:
            "midasWelcomeAvatar 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both",
        }}
      >
        <MidasLogo size="lg" />
      </Box>

      {/* Greeting */}
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "18px",
          fontWeight: 700,
          color: dt.textPrimary,
          textAlign: "center",
          mt: "16px",
          animation: "midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
        }}
      >
        {greeting}
      </Box>

      {/* Subtitle */}
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "13px",
          fontWeight: 400,
          color: dt.textSecondary,
          textAlign: "center",
          maxWidth: "300px",
          lineHeight: 1.6,
          mt: "8px",
          animation: "midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.25s both",
        }}
      >
        {translate("welcomeSubtitle")}
      </Box>

      {/* Starter suggestion grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
          gap: "8px",
          maxWidth: "340px",
          width: "100%",
          mt: "24px",
          mx: "auto",
        }}
      >
        {platformSuggestions.map((item, index) => {
          const Icon = item.icon;
          const text = translate(item.textKey);
          return (
            <Box
              key={item.textKey}
              onClick={() => onSuggestionClick(text)}
              sx={{
                backgroundColor: dt.cardBg,
                border: `1.5px solid ${dt.cardBorder}`,
                borderRadius: "12px",
                padding: "12px 14px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                transition: "all 0.2s ease",
                animation: `midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) ${
                  0.35 + index * 0.08
                }s both`,
                "&:hover": {
                  borderColor: midas.gold,
                  backgroundColor: midas.goldSoft,
                  "& .midas-chip-text": {
                    color: midas.goldDark,
                  },
                },
              }}
            >
              <Icon sx={{ fontSize: "16px", color: midas.gold }} />
              <Box
                className="midas-chip-text"
                sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 500,
                  color: dt.textSecondary,
                  lineHeight: 1.4,
                  textAlign: "left",
                }}
              >
                {text}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Machine mode picker -- visually distinct from topic suggestions */}
      <Box
        onClick={handleOpenPicker}
        sx={{
          maxWidth: "340px",
          width: "100%",
          mt: "12px",
          mx: "auto",
          backgroundColor: "rgba(212, 160, 23, 0.06)",
          border: `1.5px solid rgba(212, 160, 23, 0.35)`,
          borderRadius: "12px",
          padding: "14px 16px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          transition: "all 0.2s ease",
          animation: "midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.6s both",
          "&:hover": {
            borderColor: midas.gold,
            backgroundColor: "rgba(212, 160, 23, 0.12)",
          },
        }}
      >
        <PrecisionManufacturingRounded sx={{ fontSize: "20px", color: midas.gold }} />
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 600,
              color: midas.goldDark,
            }}
          >
            {translate("machinePicker.pickMachine")}
          </Box>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "11px",
              fontWeight: 400,
              color: dt.textSecondary,
              mt: "2px",
            }}
          >
            {translate("machinePicker.pickMachineHint")}
          </Box>
        </Box>
      </Box>

    </Box>
  );
};

export default ChatWelcome;
